import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth"; // Importa o hook de autenticação
import "./Calccafe.css";
import logo from "../../assets/calccafe.png";
import img1 from "../../assets/cafe1.jpg";
import img2 from "../../assets/cafe2.jpg";
import img3 from "../../assets/cafe3.jpg";
import img4 from "../../assets/cafe4.jpg";
import img5 from "../../assets/cafe5.jpg";

const CalcCafe = () => {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();

  // Função de clique que decide para onde o usuário deve ser redirecionado
  const handleSubscriptionClick = () => {
    if (loading) return; // Não faz nada enquanto os dados do usuário carregam

    if (!user) {
      navigate("/login"); // Se não está logado, vai para login
      return;
    }

    // Se está logado, verifica a assinatura específica do CalcCafé
    if (userData?.calccafe) {
      navigate("/calccafe/app"); // Se já tem, vai para a tela principal do app
    } else {
      navigate("/planos/calccafe"); // Se não tem, vai para os planos do CalcCafé
    }
  };

  const advantages = [
    {
      title: "Produção e Produtividade",
      content: "A produtividade no cultivo de café é medida pela quantidade de sacas produzidas por pé ou por hectare. Esse indicador é fundamental para avaliar a eficiência da operação agrícola e auxiliar na identificação do uso adequado da tecnologia disponível.",
      image: img3
    },
    {
      title: "Custo do Capital e Depreciação",
      content: "Este aspecto envolve o capital investido inicialmente na produção de café e a depreciação dos ativos utilizados, que se refere à perda de valor ao longo do tempo. A análise desses custos é crucial para entender o retorno sobre o investimento e a viabilidade econômica do negócio.",
      image: img4
    },
    {
      title: "Custos Operacionais",
      content: "Os custos operacionais englobam todas as despesas recorrentes necessárias para manter a produção de café, como mão de obra e manutenção. Controlar esses custos é vital para assegurar a lucratividade do negócio, pois impactam diretamente a margem de lucro.",
      image: img5
    },
    {
      title: "Receita e Rentabilidade",
      content: "A receita gerada pela venda do café é calculada multiplicando o preço da saca pela quantidade vendida. Esse valor deve ser suficiente para cobrir todos os custos operacionais e gerar lucro. A rentabilidade é um indicador fundamental da saúde financeira do negócio.",
      image: img1
    },
    {
      title: "Relatório Final",
      content: "O relatório final oferece uma visão integrada dos resultados calculados ou simulados, facilitando a tomada de decisões. Ele permite monitorar continuamente o desempenho do negócio, aumentando a eficiência na gestão e economizando tempo e recursos.",
      image: img2
    }
  ];

  const plans = [
    {
      name: "Mensal",
      price: "R$19,90/mês",
      features: ["Cálculos ilimitados", "Simulações ilimitadas", "Suporte disponível", "Pagamento mensal"]
    },
    {
      name: "Trimestral",
      price: "R$49,90/trimestre",
      features: ["Cálculos ilimitados", "Simulações ilimitadas", "Suporte disponível", "Pagamento a cada três meses"]
    },
    {
      name: "Anual",
      price: "R$199,90/ano",
      features: ["Cálculos ilimitados", "Simulações ilimitadas", "Suporte disponível", "Pagamento anual"]
    }
  ];

  return (
    <div className="calccafe-page-container">
      <header className="calccafe-page-header">
        <div className="calccafe-logo-container">
          <img src={logo} alt="CalcCafé Logo" className="calccafe-page-logo" />
        </div>
        <h1 className="calccafe-title">CalcCafé</h1>
        <p className="calccafe-header-subtitle">
          Com o CalcCafé você obterá independência e autonomia quando se trata das economias e finanças do seu negócio, de maneira rápida, simples, segura e eficaz.
        </p>
      </header>

      {/* Seção de imagem estática, substituindo o Slider */}
      <section className="calccafe-hero-section">
        <img src={img1} alt="Controle de Custos do Café" className="hero-image" />
        <div className="hero-caption">
          <h3>Otimize sua Lavoura, Colha seus Lucros</h3>
          <p>A ferramenta definitiva para a gestão financeira da sua produção de café.</p>
        </div>
      </section>

      <section className="calccafe-features-section">
        <div className="calccafe-feature-card">
          <h3>Controle de Custos</h3>
          <p>
            O sistema possibilita o controle dos custos, monitorando despesas com cultivo e colheita. Facilita o planejamento financeiro ao apoiar na criação de orçamentos e planos estratégicos, otimizando os recursos e potencializando os lucros.
          </p>
        </div>
        <div className="calccafe-feature-card">
          <h3>Análise de Desempenho</h3>
          <p>
            A análise de desempenho proporciona uma visão abrangente sobre o estado financeiro do negócio, identificando áreas de lucro e oportunidades de melhoria. Com foco em transparência, o sistema possibilita que todos os envolvidos acessem informações financeiras atualizadas.
          </p>
        </div>
      </section>

      <section className="calccafe-plans-section">
        <h2 className="calccafe-section-title">Confira nossos planos</h2>
        <div className="calccafe-plans-grid">
          {plans.map((plan, index) => (
            <div className="calccafe-plan-card" key={index}>
              <div className="calccafe-plan-header">
                <h3>{plan.name}</h3>
                <p className="calccafe-plan-price">{plan.price}</p>
              </div>
              <ul className="calccafe-plan-features">
                {plan.features.map((feature, i) => (<li key={i}>{feature}</li>))}
              </ul>
              {/* Botão atualizado com a nova função de clique */}
              <button onClick={handleSubscriptionClick} className="calccafe-plan-button">Assinar</button>
            </div>
          ))}
        </div>
      </section>

      <section className="calccafe-advantages-section">
        <h2 className="calccafe-section-title">Vantagens de obter o CalcCafé</h2>
        <div className="calccafe-advantages-grid">
          {advantages.map((advantage, index) => (
            <div className="calccafe-advantage-card" key={index}>
              <div className="calccafe-advantage-image">
                <img src={advantage.image} alt={advantage.title} />
              </div>
              <div className="calccafe-advantage-content">
                <h3>{advantage.title}</h3>
                <p>{advantage.content}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="calccafe-cta-container">
        {/* Botão atualizado com a nova função de clique */}
        <button onClick={handleSubscriptionClick} className="calccafe-cta-button" disabled={loading}>
          {loading ? "Carregando..." : "Comece agora"}
        </button>
        <Link to="/" className="calccafe-back-link">Voltar para Home</Link>
      </div>
    </div>
  );
};

export default CalcCafe;
